/* ============================================================
   INTIMUM — Theme JS
   Core functionality: reveal animations, header, navigation,
   accordions, quantity selectors, carousel
   ============================================================ */

(function () {
  'use strict';

  /* ── Scroll Reveal ─────────────────────────────────────── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );

  function initReveal() {
    document.querySelectorAll('.reveal, .stagger').forEach((el) => {
      revealObserver.observe(el);
    });
  }

  /* ── Header ────────────────────────────────────────────── */
  function initHeader() {
    const header = document.getElementById('site-header');
    if (!header) return;

    let lastScroll = 0;
    const scrollThreshold = 50;

    window.addEventListener('scroll', () => {
      const currentScroll = window.scrollY;
      if (currentScroll > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });

    // Search toggle
    const searchToggle = document.getElementById('search-toggle');
    const searchPanel = document.getElementById('header-search');
    const searchClose = document.getElementById('search-close');
    const searchInput = searchPanel?.querySelector('input');

    if (searchToggle && searchPanel) {
      searchToggle.addEventListener('click', () => {
        searchPanel.hidden = !searchPanel.hidden;
        if (!searchPanel.hidden && searchInput) {
          searchInput.focus();
        }
      });
      searchClose?.addEventListener('click', () => {
        searchPanel.hidden = true;
      });
    }

    // Mobile nav
    const mobileToggle = document.getElementById('mobile-menu-toggle');
    const mobileNav = document.getElementById('mobile-nav');
    const mobileClose = document.getElementById('mobile-menu-close');
    const mobileOverlay = document.getElementById('mobile-nav-overlay');

    function openMobileNav() {
      mobileNav.hidden = false;
      mobileOverlay.hidden = false;
      mobileToggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeMobileNav() {
      mobileNav.hidden = true;
      mobileOverlay.hidden = true;
      mobileToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    mobileToggle?.addEventListener('click', openMobileNav);
    mobileClose?.addEventListener('click', closeMobileNav);
    mobileOverlay?.addEventListener('click', closeMobileNav);

    // Mobile dropdown
    document.querySelectorAll('[data-mobile-dropdown]').forEach((group) => {
      const btn = group.querySelector('.mobile-nav__link--parent');
      const sub = group.querySelector('.mobile-nav__submenu');
      btn?.addEventListener('click', () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !isOpen);
        sub.hidden = isOpen;
      });
    });
  }

  /* ── Announcement Bar ──────────────────────────────────── */
  function initAnnouncementBar() {
    const bar = document.getElementById('announcement-bar');
    const closeBtn = document.getElementById('announcement-close');
    if (!bar || !closeBtn) return;

    if (sessionStorage.getItem('intimum-announcement-closed')) {
      bar.classList.add('is-hidden');
      return;
    }

    closeBtn.addEventListener('click', () => {
      bar.classList.add('is-hidden');
      sessionStorage.setItem('intimum-announcement-closed', '1');
    });
  }

  /* ── Accordions ────────────────────────────────────────── */
  function initAccordions() {
    document.querySelectorAll('.accordion__trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion__item');
        const content = item.querySelector('.accordion__content');
        const isOpen = item.classList.contains('is-open');

        // Close siblings
        const parent = item.closest('.accordion');
        parent?.querySelectorAll('.accordion__item.is-open').forEach((openItem) => {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            openItem.querySelector('.accordion__trigger').setAttribute('aria-expanded', 'false');
            openItem.querySelector('.accordion__content').style.maxHeight = '0';
          }
        });

        item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', !isOpen);
        content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
      });
    });
  }

  /* ── Quantity Selectors ────────────────────────────────── */
  function initQuantitySelectors() {
    document.querySelectorAll('[data-qty-selector]').forEach((selector) => {
      const input = selector.querySelector('[data-qty-input]');
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');

      minus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10);
        if (val > 1) input.value = val - 1;
        input.dispatchEvent(new Event('change'));
      });

      plus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10);
        if (val < 99) input.value = val + 1;
        input.dispatchEvent(new Event('change'));
      });
    });
  }

  /* ── Carousel (Bestsellers) ────────────────────────────── */
  function initCarousels() {
    const track = document.getElementById('bestsellers-track');
    const prevBtn = document.getElementById('bs-prev');
    const nextBtn = document.getElementById('bs-next');
    if (!track) return;

    const scrollAmount = 300;

    prevBtn?.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextBtn?.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
  }

  /* ── Product Card ATC ──────────────────────────────────── */
  function initProductCardATC() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-card__atc');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.productId;
      if (!variantId) return;

      btn.classList.add('btn--loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      })
        .then((res) => res.json())
        .then(() => {
          btn.classList.remove('btn--loading');
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(() => {
          btn.classList.remove('btn--loading');
        });
    });
  }

  /* ── Product Form Submit ───────────────────────────────── */
  function initProductForm() {
    const form = document.getElementById('product-form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('.product-form__atc');
      const variantId = document.getElementById('product-variant-id')?.value;
      const qty = document.getElementById('product-qty')?.value || 1;

      if (!variantId) return;

      btn.classList.add('btn--loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: parseInt(qty, 10) }] }),
      })
        .then((res) => res.json())
        .then(() => {
          btn.classList.remove('btn--loading');
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(() => {
          btn.classList.remove('btn--loading');
        });
    });
  }

  /* ── Variant Selector ──────────────────────────────────── */
  function initVariantSelector() {
    document.querySelectorAll('.swatch, .swatch-text').forEach((btn) => {
      btn.addEventListener('click', () => {
        const parent = btn.closest('.variant-option');
        parent.querySelectorAll('.swatch, .swatch-text').forEach((s) => s.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const selectedLabel = parent.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = btn.dataset.value || btn.textContent.trim();
      });
    });
  }

  /* ── Sticky ATC (mobile product page) ──────────────────── */
  function initStickyATC() {
    const productForm = document.getElementById('product-form');
    if (!productForm || window.innerWidth > 768) return;

    const stickyBar = document.createElement('div');
    stickyBar.className = 'sticky-atc';
    stickyBar.id = 'sticky-atc';

    const title = document.querySelector('.product-info__title');
    const price = document.querySelector('.product-info__price');

    stickyBar.innerHTML = `
      <div class="sticky-atc__inner">
        <div class="sticky-atc__info">
          <div class="sticky-atc__title">${title ? title.textContent : ''}</div>
          <div class="sticky-atc__price">${price ? price.textContent.trim() : ''}</div>
        </div>
        <button class="btn btn--primary btn--sm" type="button" id="sticky-atc-btn">Adicionar</button>
      </div>
    `;

    document.body.appendChild(stickyBar);

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(productForm);

    document.getElementById('sticky-atc-btn')?.addEventListener('click', () => {
      productForm.querySelector('.product-form__atc')?.click();
    });
  }

  /* ── Sort Select ───────────────────────────────────────── */
  function initSortSelect() {
    const select = document.getElementById('sort-select');
    if (!select) return;

    const url = new URL(window.location.href);
    const currentSort = url.searchParams.get('sort_by');
    if (currentSort) select.value = currentSort;

    select.addEventListener('change', () => {
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  }

  /* ── Filter Toggle (collection) ────────────────────────── */
  function initFilters() {
    const filterToggle = document.getElementById('filter-toggle');
    const filters = document.getElementById('collection-filters');
    const filtersClose = document.getElementById('filters-close');
    if (!filterToggle || !filters) return;

    filterToggle.addEventListener('click', () => {
      filters.hidden = !filters.hidden;
    });
    filtersClose?.addEventListener('click', () => {
      filters.hidden = true;
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initAnnouncementBar();
    initAccordions();
    initQuantitySelectors();
    initCarousels();
    initProductCardATC();
    initProductForm();
    initVariantSelector();
    initStickyATC();
    initSortSelect();
    initFilters();
  });
})();
